import { useEffect, useState } from "react";
import { ShoppingBag, ShoppingBasket, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { markCartSent, wasCartSent } from "../lib/cartSent";
import { openShop } from "../lib/shopCheckout";
import { useBasket } from "../context/BasketContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { QrBottomChoice, useQrDecision } from "./QrBottomChoice";

const frontView = (turntable) => (turntable || []).find((t) => t.view === "front")?.image || turntable?.[0]?.image;

// "Print this for me": one tap puts this duck (its link, size and filament list attached) into the shop's cart and opens
// the cart page, which shows the duck's own picture. For the signed-in maker's own duck it goes through the basket, so the
// cart holds every basket duck and nothing doubles up; anyone else gets the classic add-to-cart link.
export const OrderPrintButton = ({ id, height, colors, turntable, render, ready = true, canBasket = false }) => {
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [going, setGoing] = useState(false);
  const [sent, setSent] = useState(false);
  const basket = useBasket();
  const [qrOn, setQrOn] = useState(null);
  const qrDecision = useQrDecision({ creationId: id, height, owner: false, qr: qrOn });
  const qrChecked = qrOn === null ? qrDecision?.wanted !== false : qrOn;
  const { user } = useAuth();

  useEffect(() => {
    if (!ready) return;
    let alive = true;
    setBusy(true);
    setSent(wasCartSent(id, height));
    api
      .get(`/share/${id}/order`, { params: { height, colors, ...(qrOn === null ? {} : { qr: qrOn ? 1 : 0 }) } })
      .then((r) => alive && setOrder(r.data))
      .catch(() => alive && setOrder({ enabled: false }))
      .finally(() => alive && setBusy(false));
    return () => {
      alive = false;
    };
  }, [id, height, colors, ready, qrOn]);

  if (!ready || !order?.enabled) return null;
  const preview = frontView(turntable) || render;

  const viaBasket = canBasket && basket && user;

  const printNow = async () => {
    setGoing(true);
    if (viaBasket) {
      const ok = await basket.printNow(id, height, qrChecked);
      if (ok) {
        markCartSent(id, height);
        setSent(true);
      }
    } else {
      markCartSent(id, height);
      openShop(order.url);
      setSent(true);
    }
    setTimeout(() => setGoing(false), 3000); // the page is navigating; reset in case the jump was blocked
  };

  return (
    <div data-testid="order-print-card" className="rounded-3xl border-2 border-accent/40 bg-accent/5 p-5 space-y-4">
      <div className="flex gap-4 items-center">
        {preview && (
          <img src={preview} alt="your duck, painted" data-testid="order-print-preview" className="h-24 w-24 shrink-0 rounded-2xl object-cover border-2 border-background shadow" />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-heading font-bold text-lg leading-tight">Your Duck is ready to Order!</p>
          <p className="text-sm text-muted-foreground mt-1">
            {order.product} · {order.variant} · shipped from <span className="font-mono2 text-foreground">{order.store}</span>
          </p>
          <p className="mt-2 flex items-baseline gap-2">
            <span data-testid="order-print-price" className="font-heading text-2xl font-bold">{order.price}</span>
            <span className="text-xs text-muted-foreground">{order.available ? "plus shipping · price set by the shop" : "currently sold out"}</span>
          </p>
        </div>
      </div>
      {order.held && (
        <p data-testid="order-held-note" className="rounded-2xl border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-xs">
          {order.held}. Downloads stay available.
        </p>
      )}
      <Button data-testid="order-print-btn" onClick={printNow} disabled={busy || going || !order.available || !!order.held} className="w-full h-14 rounded-full text-base font-bold gap-2 duck-shadow">
        {busy || going ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShoppingBag className="h-5 w-5" />}
        {order.available ? `Print this for me · ${height} mm · ${order.price}` : "Sold out at this size"}
      </Button>
      {order.available && (
        <QrBottomChoice decision={qrDecision} checked={qrChecked} onChange={setQrOn} testid="qr-choice-order" />
      )}
      {viaBasket && order.available && !order.held && (
        <Button
          data-testid="order-add-basket-btn"
          variant="outline"
          disabled={basket.busy}
          onClick={() => basket.add([{ duck_id: id, height, qr_bottom: qrChecked }])}
          className="w-full h-12 rounded-full font-bold gap-2"
        >
          <ShoppingBasket className="h-5 w-5" /> Add to basket and check out later
        </Button>
      )}
      {sent ? (
        <p data-testid="order-sent-note" className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" /> Sent to your cart at {order.store}. {viaBasket ? "It stays in your basket until you order." : "Tap again to add another."}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">One tap puts this duck in your cart at {order.store}. Its link, size and filament list travel with the order, so the shop prints exactly what you see here.</p>
      )}
    </div>
  );
};
